import React, { type RefObject, useCallback } from "react";
import { commitMutation, graphql, readInlineData, useLazyLoadQuery } from "react-relay";

import {
	faBolt,
	faBook,
	faBullseyeArrow,
	faCalendar,
	faChevronDown,
	faChevronUp,
	faCog,
	faFileAlt,
	faSearch,
	faThumbsUp,
	faUser,
	faWindowRestore,
} from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { memoize } from "lodash";
import { Loader } from "semantic-ui-react";

import ThemeContext from "~/common/ThemeContext";
import ViewportContext from "~/common/ViewportContext";
import { getIntegrationForKey } from "~/integrations/actionItemIntegration";
import { useDebouncedValue, useID } from "~/lib/hooks";
import { EditorConfig, ProseReact } from "~/lib/prose";
import { getAllMarks } from "~/lib/prose/marks";
import { CustomEmoji, Embed, HardBreak, HorizontalRule, KeyResult, Tag, TextNode } from "~/lib/prose/nodes";
import { Header as HeaderNode } from "~/lib/prose/nodes/header";
import { ImageStrip, ImageStripItem, ImageStripItemCaption, ImageStripItemFile } from "~/lib/prose/nodes/image-strip";
import Mention from "~/lib/prose/nodes/mention";
import { BulletPoint, NumberedListItem, Unstyled } from "~/lib/prose/nodes/plaintext";
import { Agenda, Todo } from "~/lib/prose/plugins/assignable";
import BlockBoundary from "~/lib/prose/plugins/blockBoundary";
import Indent from "~/lib/prose/plugins/indent";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { Fallbacks } from "~/lib/suspense";
import { listFormat, nGetText, setTooltip } from "~/lib/utils";
import { environment } from "~/models/index";
import { ConnectedIcon } from "~/stream/ConnectedIcon";
import type { StreamNotesRef } from "~/stream/StreamNotes";
import { formatNoteDate } from "~/stream/utils";
import { Checkbox } from "~/ui";
import { Button, Tooltip } from "~/ui";
import AvatarList, { type AvatarListUser } from "~/ui/avatar/AvatarList";
import NameList from "~/ui/avatar/NameList";
import { Modal, TextField } from "~/ui/legacyFellowIndex";
import LoadingSpinner from "~/ui/loading/LoadingSpinner";
import { __globalModalContext, useModals } from "~/ui/modals";
import toast from "~/ui/toast";

import AppContext from "../common/AppContext";
import { ReactComponent as EmptyStateIlloDark } from "./css/merge-stream-empty-state-illo-dark.svg";
import { ReactComponent as EmptyStateIllo } from "./css/merge-stream-empty-state-illo.svg";

import type { MergeStream_StreamIntegration$key as StreamIntegration$key } from "~/__generated__/MergeStream_StreamIntegration.graphql";
import type { MergeStream_SummaryQuery as SummaryQuery } from "~/__generated__/MergeStream_SummaryQuery.graphql";
import type {
	MergeStreamMutation as MergeStreamMutationType,
	StreamAccessType as NoteStreamUserAccessType,
} from "~/__generated__/MergeStreamMutation.graphql";
import type { MergeStreamPreviewQuery } from "~/__generated__/MergeStreamPreviewQuery.graphql";
import type { MergeStreamQuery, NoteStreamType } from "~/__generated__/MergeStreamQuery.graphql";

import styles from "./css/MergeStream.module.css";

export const MERGE_INTENT_ANALYTICS_EVENT = "Merge Modal Opened";
const MERGE_DISMISSAL_ANALYTICS_EVENT = "Merge Modal Closed";
const MERGE_SUCCESS_ANALYTICS_EVENT = "Merge note stream";

const getEditorConfig = memoize(
	(currentUserId: string | undefined, accountId: string | undefined) =>
		new EditorConfig({
			extensions: [
				new BlockBoundary(),
				...getAllMarks(),
				new TextNode(),
				new Indent(),
				new Unstyled(),
				new BulletPoint(),
				new NumberedListItem(),
				new ImageStrip({ contentId: "", disableWhenNotEditable: true }),
				new ImageStripItem(),
				new ImageStripItemFile(),
				new ImageStripItemCaption(),
				new HardBreak(),
				new HorizontalRule(),
				new HeaderNode(),
				new Tag({ showToolbar: false, clickable: false }),
				new Todo({ attendees: [], disabledReadOnly: true, viewerId: currentUserId }),
				new Agenda({ attendees: [], disabledReadOnly: true, viewerId: currentUserId }),
				new Mention({ currentUserId }),
				new Embed(),
				new KeyResult(),
				new CustomEmoji({ accountId }),
			],
			nodes: {
				doc: {
					content: "block+",
				},
			},
		}),
);

const MergeStreamMutation = graphql`
	mutation MergeStreamMutation($input: MergeNoteStreamInput!) {
		mergeNoteStream(input: $input) {
			homePanelGroups {
				...HomePanelGroup_group
			}
			sourceStream {
				id
				starred
				starOrder
				userCount

				# Update streams in relay store to filter out from home panel
				mergedTo {
					id
				}

				notestreamusers {
					id
					accessType
					user {
						id
						email
						image
						fullName
						lastName
						firstName
						isActive
						isConfigured
						isGuest
						hasReceivedInvites
					}
				}

				tabs {
					...TabsAndBookmarks_noteStreamTab @relay(mask: false)
				}
			}
			targetStream {
				id
				starred
				starOrder
				userCount

				notestreamusers {
					id
					accessType
					user {
						id
						email
						image
						fullName
						lastName
						firstName
						isActive
						isConfigured
						isGuest
						hasReceivedInvites
					}
				}

				tabs {
					...TabsAndBookmarks_noteStreamTab @relay(mask: false)
				}
			}
		}
	}
`;

function StreamType({ type, userCount }: { type: NoteStreamType; userCount: number }) {
	switch (type) {
		case "goalset":
			return <>Priorities</>;
		case "meeting":
			return <>Meeting</>;
		case "oneonone":
			return <>1-on-1</>;
		case "personal":
			return <>{userCount > 1 ? "Shared" : "Private"}</>;
	}
}

interface ResultProps {
	id: string;
	type: NoteStreamType;
	displayName: string;
	notesCount: number;
	displayedUsers: AvatarListUser[];
	totalUsers: number;
	selected: boolean;
	onChange: (streamId: string | null, streamRank: number) => void;
	userAccess: NoteStreamUserAccessType;
	isExternal: boolean;
	idx: number;
}

const Result = ({
	id,
	type,
	displayName,
	notesCount,
	displayedUsers,
	totalUsers,
	selected,
	onChange,
	userAccess,
	isExternal,
	idx,
}: ResultProps) => {
	const inputId = useID();

	const canMerge = ["full", "shared"].includes(userAccess);
	const noAccessTooltip = !canMerge ? setTooltip("You have partial access to this series", { delayed: true }) : null;

	return (
		<li className={styles.resultContainer} {...noAccessTooltip}>
			<input
				id={inputId}
				type="radio"
				name="merge-streams-stream-select"
				value={id}
				className={styles.invisibleRadioButton}
				checked={selected}
				onChange={e => onChange(e.currentTarget.value, idx)}
				disabled={!canMerge}
			/>
			<label htmlFor={inputId} className={styles.result} role="option">
				<AvatarList
					className={styles.resultAttendees}
					size="small"
					users={displayedUsers}
					maxLength={2}
					hideDetailsTooltip={true}
				/>
				<p className={styles.resultTitle} title={displayName}>
					{isExternal && <ConnectedIcon style={{ marginRight: "0.5rem" }} />}
					{displayName}
				</p>
				<div className={styles.resultTags}>
					<ResultTag>
						<StreamType type={type} userCount={totalUsers} />
					</ResultTag>
					<ResultTag>
						{notesCount} note{notesCount === 1 ? null : "s"}
					</ResultTag>
				</div>
			</label>
		</li>
	);
};

export type Stream = {
	id: string;
	displayName: string;
};
interface MergeStreamModalProps {
	onClose: (merge: boolean) => void;
	stream: Stream;
}

interface MergeStreamModalState {
	searchQuery: string;
	selectedStreamId: string | null;
	streamSelectionsKey: number;
	selectedStreamRank: number | null;
}

export function useMergeStreamModal(stream: Stream, streamNotesRef?: RefObject<StreamNotesRef>) {
	const modals = useModals();

	const openModal = useCallback(async () => {
		dispatchAnalyticsTrack(MERGE_INTENT_ANALYTICS_EVENT, {
			source: "menu",
		});
		const result = await modals.open(({ close }) => <MergeStreamModal onClose={close} stream={stream} />);

		if (result && streamNotesRef) streamNotesRef.current?.refetch();
	}, [modals, stream, streamNotesRef]);
	return openModal;
}

export default class MergeStreamModal extends React.Component<MergeStreamModalProps, MergeStreamModalState> {
	static contextType = ViewportContext;
	declare context: React.ContextType<typeof ViewportContext>;

	editorConfig: EditorConfig = new EditorConfig({
		extensions: [
			...getAllMarks(),
			new TextNode(),
			new Indent(),
			new Unstyled(),
			new ImageStrip({ contentId: "", disableWhenNotEditable: true }),
			new ImageStripItem(),
			new ImageStripItemFile(),
			new ImageStripItemCaption(),
			new HardBreak(),
			new HorizontalRule(),
			new HeaderNode(),
			new Tag({ showToolbar: false, clickable: false }),
			new Todo({
				attendees: [],
			}),
			new Agenda({
				attendees: [],
			}),
			new Mention(),
			new Embed(),
			new KeyResult(),
			new CustomEmoji(),
		],
		nodes: {
			doc: {
				content: "block+",
			},
		},
	});

	constructor(props: MergeStreamModalProps) {
		super(props);
		this.state = {
			searchQuery: "",
			selectedStreamId: null,
			streamSelectionsKey: 0,
			selectedStreamRank: null,
		};
	}

	private refetchStreamSelections() {
		this.setState(({ streamSelectionsKey }) => ({ streamSelectionsKey: streamSelectionsKey + 1 }));
	}

	private onSearchQueryChange = (value: string) => {
		this.setState({ searchQuery: value });
	};

	private onSelectedStreamChange = (streamId: string | null, streamRank: number | null) => {
		this.setState({ selectedStreamId: streamId, selectedStreamRank: streamRank });
	};

	private cancel = () => {
		dispatchAnalyticsTrack(MERGE_DISMISSAL_ANALYTICS_EVENT);

		this.props.onClose(false);
	};

	onMergeSubmit = async () => {
		const destinationStream = this.props.stream;
		const sourceStreamId = this.state.selectedStreamId;

		if (!sourceStreamId) return;

		const res = await __globalModalContext?.open<SummaryModalResult>(({ close }) => (
			<SummaryModal sourceStreamId={sourceStreamId} destinationStreamId={destinationStream.id} onClose={close} />
		));

		if (!res) return;

		if (res.action === "merge") {
			dispatchAnalyticsTrack(MERGE_SUCCESS_ANALYTICS_EVENT, {
				target: destinationStream.id,
				source: sourceStreamId,
				// eslint-disable-next-line camelcase
				share_with_partial_users: res.shareWithPartialUsers,
				// eslint-disable-next-line camelcase
				from_search_query: this.state.searchQuery ? true : false,
				// eslint-disable-next-line camelcase
				source_stream_rank: this.state.selectedStreamRank === null ? null : this.state.selectedStreamRank + 1,
			});
			commitMutation<MergeStreamMutationType>(environment, {
				mutation: MergeStreamMutation,
				variables: {
					input: {
						targetStreamId: destinationStream.id,
						sourceStreamId,
						shareWithPartialUsers: res.shareWithPartialUsers,
						mergeTabs: res.mergeTabs,
					},
				},
				onCompleted: (response, errors) => {
					if (errors) {
						toast.error("We had a problem merging your notes, try again");
						if (errors.some(e => e.message === "CONCURRENT_MERGE")) {
							this.refetchStreamSelections();
						}
						throw new Error((errors || []).map(e => e.message).join(";"));
					}
					toast.success("Notes merged successfully");
					this.props.onClose(true);
				},
			});
		}
	};

	public render() {
		const { isMobile } = this.context;

		const nextButtonDisabled = this.state.selectedStreamId === null;
		const nextButton = (
			<Button
				size="normal"
				variant="primary"
				disabled={nextButtonDisabled}
				className={styles.nextButton}
				onClick={this.onMergeSubmit}
				data-test-id="merge-stream-merge-button"
			>
				Next
			</Button>
		);

		return (
			<Modal onClose={this.cancel} data-test-id="modal" width="62.5rem" className={styles.modal}>
				<Modal.Header onClose={this.cancel}>
					<Modal.Title>Merge Notes</Modal.Title>
				</Modal.Header>
				<Modal.Body dark noPadding className={styles.modalBody}>
					<div className={styles.container}>
						<div className={styles.left}>
							<SearchBox query={this.state.searchQuery} onQueryChange={this.onSearchQueryChange} />
							<div className={styles.streamSelections} role="listbox">
								<StreamSelections
									fetchKey={this.state.streamSelectionsKey}
									streamId={this.props.stream.id}
									searchQuery={this.state.searchQuery}
									selectedStreamId={this.state.selectedStreamId}
									onChange={this.onSelectedStreamChange}
								/>
							</div>
						</div>
						{isMobile ? null : this.state.selectedStreamId ? (
							<Preview sourceStreamId={this.state.selectedStreamId} destinationStreamId={this.props.stream.id} />
						) : (
							<PreviewPlaceholder displayName={this.props.stream.displayName} />
						)}
					</div>
				</Modal.Body>
				<Modal.Footer>
					<Button size="normal" variant="secondary" onClick={this.cancel}>
						Cancel
					</Button>
					<Tooltip disabled={!nextButtonDisabled} content="Select a series to be merged to continue">
						{/* Tippy does not show up on disabled elements for reasons, so we
						    need to wrap the button in something that's not disabled for the
						    tooltip to show up. */}
						{nextButtonDisabled ? <div>{nextButton}</div> : nextButton}
					</Tooltip>
				</Modal.Footer>
			</Modal>
		);
	}
}

interface SearchBoxProps {
	query: string;
	onQueryChange: (query: string) => void;
}

const searchIcon = <FontAwesomeIcon icon={faSearch} />;

export function SearchBox({ query, onQueryChange }: SearchBoxProps) {
	const onChange = React.useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			onQueryChange(e.currentTarget.value);
		},
		[onQueryChange],
	);

	const onClear = React.useCallback(() => {
		onQueryChange("");
	}, [onQueryChange]);

	return (
		<div className={styles.searchWrapper}>
			<TextField
				className={styles.searchBox}
				icon={searchIcon}
				placeholder="Search notes"
				withCross
				value={query}
				onChange={onChange}
				onClear={onClear}
			/>
		</div>
	);
}

function NoSearchResults() {
	return (
		<div className={styles.noSearchResults}>
			<p>No notes found</p>
		</div>
	);
}

interface PreviewPlaceholderProps {
	displayName: string;
}

export function PreviewPlaceholder({ displayName }: PreviewPlaceholderProps) {
	const { isMobile } = React.useContext(ViewportContext);
	const { isDarkMode } = React.useContext(ThemeContext);

	if (isMobile) {
		return null;
	}
	return (
		<div className={cx(styles.right, styles.content, styles.emptyStateContainer)}>
			<div className={styles.previewPlaceholder}>
				{isDarkMode ? <EmptyStateIlloDark /> : <EmptyStateIllo />}
				<p>
					Select a series whose notes will be moved into <strong>{displayName}</strong>
				</p>
			</div>
		</div>
	);
}

interface PreviewProps {
	sourceStreamId: string;
	destinationStreamId: string;
}

export function Preview(props: PreviewProps) {
	return (
		<div className={cx(styles.right, styles.content)}>
			<Fallbacks error={<div>Failed to load series preview</div>} loading={<Loader active />}>
				<PreviewInner {...props} />
			</Fallbacks>
		</div>
	);
}

function PreviewInner({ sourceStreamId, destinationStreamId }: PreviewProps) {
	const { user, account } = React.useContext(AppContext);
	const data = useLazyLoadQuery<MergeStreamPreviewQuery>(
		graphql`
			query MergeStreamPreviewQuery($sourceStreamId: ID!, $destinationStreamId: ID!) {
				sourceStream: noteStream(id: $sourceStreamId) {
					id
					type
					displayName
					notesCount
					notestreamusers {
						id
						accessType
						user {
							id
							fullName
							isGuest
						}
					}
					notes {
						edges {
							node {
								id
								rawDraftJson
								# formatNoteDate
								createdAt
								eventStart
								eventEnd
								noteType
							}
						}
					}
				}

				destinationStream: noteStream(id: $destinationStreamId) {
					id
					displayName

					notestreamusers {
						id
						accessType
						user {
							id
							fullName
							isGuest
						}
					}
				}
			}
		`,
		{ sourceStreamId, destinationStreamId },
		{ fetchPolicy: "store-and-network" },
	);

	return (
		// This key is to tell React to use a new div when the sourceStreamId
		// changes. As a result, scroll position won't be maintained between
		// selected streams.
		<div key={sourceStreamId} className={styles.previewWrapper}>
			{data.sourceStream?.notes?.edges?.map(
				item =>
					item?.node && (
						<PreviewNoteCard key={item.node.id} note={item.node}>
							<ProseReact
								config={getEditorConfig(user?.id, account?.id)}
								editable={false}
								context={{ participants: [], note: {} }}
								initialDoc={item.node.rawDraftJson.doc}
							/>
						</PreviewNoteCard>
					),
			)}
		</div>
	);
}

interface StreamSelectionsProps {
	searchQuery: string;
	streamId: string;
	selectedStreamId: string | null;
	includeOneOnOnes?: boolean;
	includeExternal?: boolean;
	ignorePartialAccess?: boolean;
	onChange: (streamId: string | null, selectedStreamRank: number | null) => void;
	fetchKey?: number | string;
}

function StreamSelectionsInner({
	searchQuery,
	streamId,
	selectedStreamId,
	includeOneOnOnes,
	includeExternal,
	ignorePartialAccess,
	onChange,
	fetchKey,
}: StreamSelectionsProps) {
	const debouncedSearchQuery = useDebouncedValue(searchQuery, 300);

	const data = useLazyLoadQuery<MergeStreamQuery>(
		graphql`
			query MergeStreamQuery(
				$eventTitle: String
				$streamId: ID!
				$includeOneOnOnes: Boolean
				$includeExternal: Boolean
			) {
				noteStreamsSearch(
					eventTitle: $eventTitle
					streamId: $streamId
					includeOneOnOnes: $includeOneOnOnes
					includeExternal: $includeExternal
				) {
					edges {
						node {
							id
							type
							displayName
							notesCountForViewer
							isExternal

							viewerNotestreamuser {
								accessType
							}

							userCount
							notestreamusers(first: 3) {
								user {
									# UserAttrs
									id
									email
									image
									fullName
									isActive
									isConfigured
									isGuest
									isExternal
								}
							}
						}
					}
				}
			}
		`,
		{ eventTitle: debouncedSearchQuery, streamId, includeOneOnOnes, includeExternal },
		{ fetchPolicy: "store-and-network", fetchKey },
	);

	React.useEffect(() => {
		if (selectedStreamId !== null && !data.noteStreamsSearch?.edges.some(edge => edge?.node?.id === selectedStreamId)) {
			onChange(null, null);
		}
	}, [data, onChange, selectedStreamId]);

	if (!data?.noteStreamsSearch?.edges || data.noteStreamsSearch.edges.length === 0) {
		// There is a search query but there are no results
		if (debouncedSearchQuery) {
			return <NoSearchResults />;
		}

		return null;
	}

	return (
		<>
			{data.noteStreamsSearch.edges.map(
				(item, idx) =>
					item?.node?.displayName && (
						<Result
							key={item.node.id}
							id={item.node.id}
							type={item.node.type}
							notesCount={item.node.notesCountForViewer}
							displayedUsers={item.node.notestreamusers.map(nsu => nsu.user)}
							totalUsers={item.node.userCount}
							displayName={item.node.displayName}
							selected={selectedStreamId === item.node.id}
							onChange={onChange}
							userAccess={ignorePartialAccess ? "full" : (item.node.viewerNotestreamuser?.accessType ?? "partial")}
							isExternal={item.node.isExternal}
							idx={idx}
						/>
					),
			)}
		</>
	);
}

export function StreamSelections(props: StreamSelectionsProps) {
	return (
		<Fallbacks error={<div>Failed to load search results</div>} loading={<Loader active />}>
			<StreamSelectionsInner {...props} />
		</Fallbacks>
	);
}

function ResultTag({ children }: React.PropsWithChildren<{}>) {
	return <span className={styles.tag}>{children}</span>;
}

function PreviewNoteCard({ children, note }: React.PropsWithChildren<{ note: Parameters<typeof formatNoteDate>[0] }>) {
	return (
		<article className={styles.noteCard}>
			<header className={styles.noteCardHeader}>
				<div className={styles.noteCardTime}>{formatNoteDate(note) || "Unscheduled"}</div>
				<div className={styles.previewLabel}>Preview</div>
			</header>
			<main className={styles.noteCardContent}>{children}</main>
		</article>
	);
}

type SummaryModalResult =
	| { action: "cancel" }
	| { action: "merge"; shareWithPartialUsers: boolean; mergeTabs: boolean };

interface SummaryModalProps {
	sourceStreamId: string;
	destinationStreamId: string;
	onClose: (action: SummaryModalResult) => void;
}

function SummaryModal({ onClose, ...props }: SummaryModalProps) {
	const onCancel = React.useCallback(() => onClose({ action: "cancel" }), [onClose]);

	return (
		<Modal onClose={onCancel}>
			<Modal.Header noBottomBorder className={styles.summaryModalHeader} onClose={onCancel}>
				<Modal.Title>Merge notes?</Modal.Title>
			</Modal.Header>
			<React.Suspense
				fallback={
					<Modal.Body className={styles.summaryModalBody}>
						<LoadingSpinner dimmer={false} />
					</Modal.Body>
				}
			>
				<SummaryModalInner {...props} onClose={onClose} />
			</React.Suspense>
		</Modal>
	);
}

function streamIntegration(streamKey: StreamIntegration$key) {
	const stream = readInlineData(
		graphql`
			fragment MergeStream_StreamIntegration on NoteStream @inline {
				actionItemIntegration {
					integrationProvider
				}
				asanaStream {
					id
				}
			}
		`,
		streamKey,
	);

	// The type generated for __typename on non-union non-interface types is just
	// "string", so if we want a discriminated union we need to do it manually.
	if (stream.actionItemIntegration) {
		return {
			...stream.actionItemIntegration,
			__typename: "StreamActionItemIntegrationNode" as const,
		};
	} else if (stream.asanaStream) {
		return {
			...stream.asanaStream,
			__typename: "AsanaStreamNode" as const,
		};
	}
	return null;
}

function formatStreamIntegration(streamKey: StreamIntegration$key): string {
	const integration = streamIntegration(streamKey);

	if (!integration) return "";

	if (integration.__typename === "StreamActionItemIntegrationNode") {
		return getIntegrationForKey(integration.integrationProvider).displayName;
	} else if (integration.__typename === "AsanaStreamNode") {
		return "Asana";
	}
	return "";
}

function SummaryModalInner({ sourceStreamId, destinationStreamId, onClose }: SummaryModalProps) {
	const {
		destinationStream,
		viewer: { user: self },
		sourceStream,
	} = useLazyLoadQuery<SummaryQuery>(
		graphql`
			query MergeStream_SummaryQuery($sourceStreamId: ID!, $destinationStreamId: ID!) {
				viewer {
					user {
						id
					}
				}

				destinationStream: noteStream(id: $destinationStreamId) {
					displayName
					hasAnyAutomationActive
					objectivesTabEnabled

					claimedEvents {
						id
					}

					feedbackList(onlyMeetingFeedback: false) {
						edges {
							node {
								id
							}
						}
					}

					notestreamusers {
						accessType

						user {
							id
							fullName
							isExternal
						}
					}

					template {
						id
					}

					tabs {
						...TabsAndBookmarks_noteStreamTab @relay(mask: false)
					}

					...MergeStream_StreamIntegration
				}

				sourceStream: noteStream(id: $sourceStreamId) {
					displayName
					hasAnyAutomationActive
					notesCount
					objectivesTabEnabled

					claimedEvents {
						title
					}

					feedbackList(onlyMeetingFeedback: false) {
						edges {
							node {
								id
							}
						}
					}

					notestreamusers {
						accessType

						user {
							id
							fullName
							isExternal
						}
					}

					template {
						id
					}

					tabs {
						...TabsAndBookmarks_noteStreamTab @relay(mask: false)
					}

					...MergeStream_StreamIntegration
				}
			}
		`,
		{ sourceStreamId, destinationStreamId },
	);

	const [shareWithPartialUsers, setShareWithPartialUsers] = React.useState(false);
	const onShareWithPartialUsersChange = React.useCallback((checked: boolean) => {
		setShareWithPartialUsers(checked);
	}, []);

	const [mergeTabs, setMergeTabs] = React.useState(true);
	const onMergeTabsChange = React.useCallback((checked: boolean) => {
		setMergeTabs(checked);
	}, []);

	const onConfirm = React.useCallback(
		() => onClose({ action: "merge", shareWithPartialUsers, mergeTabs }),
		[onClose, shareWithPartialUsers, mergeTabs],
	);
	const onCancel = React.useCallback(() => onClose({ action: "cancel" }), [onClose]);

	if (!destinationStream || !sourceStream) return null;

	const sourceStreamName = sourceStream.displayName || <i>Untitled series</i>;
	const destinationStreamName = destinationStream.displayName || <i>Untitled series</i>;

	type NoteStreamUser = NonNullable<typeof sourceStream>["notestreamusers"][number];
	type AccessType = NoteStreamUser["accessType"];
	type User = NoteStreamUser["user"];

	// Maps the union of both streams' notestreamusers to a pair containing their
	// access types for each stream (null if no access).
	const userAccessMap: {
		[userId: string]: [sourceStreamAccess: AccessType | null, destinationStreamAccess: AccessType | null];
	} = {};
	const usersById: { [userId: string]: User } = {};

	// Populate the user access map and users by ID with the note stream users
	// from both source and destination streams.
	for (const nsu of sourceStream.notestreamusers) {
		(userAccessMap[nsu.user.id] ??= [null, null])[0] = nsu.accessType;
		usersById[nsu.user.id] ??= nsu.user;
	}
	for (const nsu of destinationStream.notestreamusers) {
		(userAccessMap[nsu.user.id] ??= [null, null])[1] = nsu.accessType;
		usersById[nsu.user.id] ??= nsu.user;
	}

	// Figure out which users will be gaining access to new notes (i.e. they are
	// becoming shared notestreamusers) and which users will only have partial
	// access after the merge.
	const usersGainingAccess = [],
		usersWithPartialAccess = [],
		usersWithFullAccessAlready = [];
	for (const [userId, [sourceAccess, destAccess]] of Object.entries(userAccessMap)) {
		const user = usersById[userId];
		// Exclude the current user from all of these lists. For them to be able to
		// merge the streams, they must have shared or full access to both streams.
		if (user.id === self.id) continue;

		// Users with shared access in only one stream and not full access in the
		// other will gain access to all notes in the stream they had partial or no
		// access to from the other stream.
		if (sourceAccess === "shared" && destAccess !== "shared" && destAccess !== "full") {
			usersGainingAccess.push(user);
		} else if (destAccess === "shared" && sourceAccess !== "shared" && sourceAccess !== "full") {
			usersGainingAccess.push(user);
		} else if (["partial", null].includes(sourceAccess) || ["partial", null].includes(destAccess)) {
			// Users with partial or no access to either stream (and don't have
			// shared access to either) will have partial access after the merge.
			usersWithPartialAccess.push(user);
		} else {
			// sourceAccess and destAccess are both either "full" or "shared", so the
			// user already had access to all notes from both streams.
			usersWithFullAccessAlready.push(user);
		}
	}

	const externalUsersGainingAccess = usersGainingAccess.some(u => u.isExternal);

	return (
		<>
			<Modal.Body noPadding className={styles.summaryModalBody}>
				<p className={styles.summaryModalText}>
					Please review and confirm the permanent changes that will occur on the 2 series:
				</p>
				<div className={styles.summaryModalChanges}>
					<ChangeCard icon={faBook} title="Notes">
						<p>
							<strong>{sourceStreamName}</strong> will be deleted and its {sourceStream.notesCount}{" "}
							{nGetText(sourceStream.notesCount, "note", "notes")} will be moved to{" "}
							<strong>{destinationStreamName}</strong>.
						</p>
					</ChangeCard>
					{(usersGainingAccess.length > 0 ||
						usersWithPartialAccess.length > 0 ||
						usersWithFullAccessAlready.length > 0) && (
						<ChangeCard icon={faUser} title="User access">
							{usersGainingAccess.length > 0 && (
								<>
									<p>
										<strong>
											<NameList capitalize hideOtherNamesTooltip={false} maxLength={5} users={usersGainingAccess} />
										</strong>{" "}
										will gain access to all notes from both series.
									</p>
									{externalUsersGainingAccess && (
										<p>
											<ConnectedIcon style={{ marginRight: "0.25rem" }} /> Some users are from other workspaces. By
											merging these series, you will give them access to all notes in both series.
										</p>
									)}
								</>
							)}
							{usersWithPartialAccess.length > 0 ? (
								<>
									<p>
										<strong>
											<NameList capitalize hideOtherNamesTooltip={false} maxLength={5} users={usersWithPartialAccess} />
										</strong>{" "}
										will only be able to access the notes they previously had access to. Share the series to give them
										access to all notes.
									</p>
									<label className={styles.shareWithCheckboxLabel}>
										<Checkbox
											checked={shareWithPartialUsers}
											onCheckedChange={onShareWithPartialUsersChange}
											className={styles.shareWithCheckbox}
										/>
										Share series with <NameList maxLength={4} users={usersWithPartialAccess} />
									</label>
								</>
							) : usersWithFullAccessAlready.length ? (
								<p>
									<strong>
										<NameList
											capitalize
											hideOtherNamesTooltip={false}
											maxLength={5}
											users={usersWithFullAccessAlready}
										/>
									</strong>{" "}
									will continue to have access to all notes.
								</p>
							) : null}
						</ChangeCard>
					)}
					{sourceStream.tabs.find(t => !["notes", "action-items", "feedback", "objectives"].includes(t.id)) && (
						<ChangeCard icon={faWindowRestore} title="Bookmarks">
							<p>
								Bookmarks from <strong>{sourceStreamName}</strong> will {!mergeTabs && <em>not </em>}be added to{" "}
								<strong>{destinationStreamName}</strong>.
							</p>
							<label className={styles.shareWithCheckboxLabel}>
								<Checkbox
									checked={mergeTabs}
									onCheckedChange={onMergeTabsChange}
									className={styles.shareWithCheckbox}
								/>
								Retain bookmarks from <strong>{sourceStreamName}</strong>
							</label>
						</ChangeCard>
					)}
					{(!!streamIntegration(sourceStream) || !!streamIntegration(destinationStream)) && (
						<CollapsibleChangeCard icon={faCog} title="Integrations">
							<p>
								{!!streamIntegration(destinationStream) && (
									<>
										Going forward, {/* force line wrap for eslint max-len */}
										the {formatStreamIntegration(destinationStream)} integration in {destinationStreamName} will be
										applied to new items.
									</>
								)}
								{!!streamIntegration(destinationStream) && !!streamIntegration(sourceStream) && " "}
								{!!streamIntegration(sourceStream) && <>The existing items with integrations will be carried over.</>}
							</p>
						</CollapsibleChangeCard>
					)}
					{(sourceStream.hasAnyAutomationActive || destinationStream.hasAnyAutomationActive) && (
						<CollapsibleChangeCard icon={faBolt} title="Automations">
							<p>The automations in {destinationStreamName} will be retained and applied to all notes.</p>
						</CollapsibleChangeCard>
					)}
					{sourceStream.claimedEvents.length > 0 && (
						<CollapsibleChangeCard icon={faCalendar} title="Calendar events">
							<p>
								{destinationStreamName} will be associated with{" "}
								{listFormat([...new Set(sourceStream.claimedEvents.map(e => e.title))].sort())}
							</p>
						</CollapsibleChangeCard>
					)}
					{(!!sourceStream.template || !!destinationStream.template) && (
						<CollapsibleChangeCard icon={faFileAlt} title="Template">
							<p>The template in {destinationStreamName} will be retained and applied to all new notes.</p>
						</CollapsibleChangeCard>
					)}
					{(sourceStream.feedbackList.edges.length > 0 || destinationStream.feedbackList.edges.length > 0) && (
						<CollapsibleChangeCard icon={faThumbsUp} title="Feedback">
							<p>The feedback for the 2 series will be merged.</p>
						</CollapsibleChangeCard>
					)}
					{(sourceStream.objectivesTabEnabled || destinationStream.objectivesTabEnabled) && (
						<CollapsibleChangeCard icon={faBullseyeArrow} title="Objectives">
							<p>Objective filters for {destinationStreamName} will be retained.</p>
						</CollapsibleChangeCard>
					)}
				</div>
			</Modal.Body>
			<Modal.Footer>
				<Button size="normal" variant="secondary" onClick={onCancel}>
					Cancel
				</Button>
				<Button size="normal" variant="primary" onClick={onConfirm} data-test-id="merge-stream-confirmation-yes-button">
					Merge series
				</Button>
			</Modal.Footer>
		</>
	);
}

function ChangeCard({
	icon,
	title,
	children,
}: React.PropsWithChildren<{
	title: React.ReactNode;
	icon: IconDefinition;
}>) {
	return (
		<article className={styles.changeCard}>
			<header className={styles.changeCardHeader}>
				<h1 className={styles.changeCardHeading}>
					<FontAwesomeIcon fixedWidth icon={icon} className={styles.changeCardIcon} />
					{title}
				</h1>
			</header>
			<main className={styles.changeCardBody}>{children}</main>
		</article>
	);
}

function CollapsibleChangeCard({
	icon,
	title,
	children,
}: React.PropsWithChildren<{
	title: React.ReactNode;
	icon: IconDefinition;
}>) {
	return (
		<details className={cx(styles.changeCard, styles.collapsibleChangeCard)}>
			<summary className={styles.collapsibleChangeCardSummary}>
				<FontAwesomeIcon icon={faChevronDown} className={styles.collapsibleChangeCardCaretDown} />
				<FontAwesomeIcon icon={faChevronUp} className={styles.collapsibleChangeCardCaretUp} />
				<p className={styles.changeCardHeading}>
					<FontAwesomeIcon fixedWidth icon={icon} className={styles.changeCardIcon} /> {title}
				</p>
			</summary>
			<div className={styles.changeCardBody}>{children}</div>
		</details>
	);
}
